import React, { useState } from "react";
import { useWhatsApp } from "../../context/WhatsAppContext";

const TYPE_COLORS = {
  'Tip / Advice':        { bg: '#eff6ff', color: '#1d4ed8' },
  'Lifestyle / Awareness': { bg: '#f0fdf4', color: '#15803d' },
  'Success Story':       { bg: '#fdf4ff', color: '#7e22ce' },
  'Authority':           { bg: '#fff7ed', color: '#c2410c' },
  'Reminder':            { bg: '#fefce8', color: '#a16207' },
  'Welcome':             { bg: '#f0fdfa', color: '#0f766e' },
  'Follow-up':           { bg: '#f0f9ff', color: '#0369a1' },
};

const chipStyle = (type) => {
  const c = TYPE_COLORS[type] || { bg: '#f3f4f6', color: '#374151' };
  return { fontSize: 10, background: c.bg, color: c.color, padding: '1px 7px', borderRadius: 10, fontWeight: 700, flexShrink: 0 };
};

/* ─────────────────────────────────────────────────────
   KITDueDashboard
   Props:
     dueMessages     — [{ lead, msg }] where msg is overdue/due today
     upcomingMessages— [{ lead, msg }] due within next 3 days
     onSend(lead, scheduleId, sentText) — after user sends a msg
     onSkip(lead, scheduleId)           — mark as skipped
────────────────────────────────────────────────────── */
export default function KITDueDashboard({ dueMessages, upcomingMessages, onSend, onSkip }) {
  const { sendWhatsApp, config: waConfig } = useWhatsApp();
  const [expanded, setExpanded] = useState(true);
  const [sendPreview, setSendPreview] = useState(null); // { lead, msg }
  const [sending, setSending] = useState(false);

  if (!dueMessages.length && !upcomingMessages.length) return null;

  const total = dueMessages.length + upcomingMessages.length;

  const handleSendClick = (lead, msg) => {
    setSendPreview({ lead, msg });
  };

  const confirmSend = async () => {
    if (!sendPreview) return;
    const { lead, msg } = sendPreview;
    const name = lead.enquiredForName || lead.name || 'Patient';
    const text = `Namaste ${name} Ji! ${msg.text}`;
    setSending(true);
    await sendWhatsApp(lead.number, text);
    onSend(lead, msg.scheduleId, text);
    setSendPreview(null);
    setSending(false);
  };

  return (
    <>
      {/* Send Preview Mini-Modal */}
      {sendPreview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99998,
        }} onClick={() => setSendPreview(null)}>
          <div style={{
            background: '#fff', borderRadius: 12, width: 460, maxWidth: '94vw',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#25D366', color: '#fff', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>💬 Send KIT Message</div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  {sendPreview.lead.enquiredForName || sendPreview.lead.name} · {sendPreview.lead.number}
                </div>
              </div>
              <button onClick={() => setSendPreview(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 20 }}>{sendPreview.msg.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{sendPreview.msg.title}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>Day {sendPreview.msg.day} · {sendPreview.msg.campaignName}</div>
                </div>
                <span style={chipStyle(sendPreview.msg.type)}>{sendPreview.msg.type}</span>
              </div>
              <div style={{ background: '#f0fdf9', border: '1px solid #6ee7b7', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#1a4731', lineHeight: 1.6 }}>
                <div style={{ color: '#888', fontSize: 10, marginBottom: 4 }}>💬 WHATSAPP PREVIEW</div>
                Namaste {sendPreview.lead.enquiredForName || sendPreview.lead.name} Ji!{' '}
                {sendPreview.msg.text}
              </div>
            </div>
            <div style={{ padding: '10px 18px 14px', display: 'flex', gap: 8, borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={confirmSend}
                disabled={sending}
                style={{ flex: 2, padding: '9px 14px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                {sending ? '⏳ Sending…' : waConfig.provider === 'manual' ? '💬 Open WhatsApp & Mark Sent' : `💬 Send via ${waConfig.provider.toUpperCase()} & Mark Sent`}
              </button>
              <button
                onClick={() => { onSkip(sendPreview.lead, sendPreview.msg.scheduleId); setSendPreview(null); }}
                style={{ flex: 1, padding: '9px 14px', background: '#f3f4f6', color: '#6b7280', border: '1.5px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Skip
              </button>
              <button
                onClick={() => setSendPreview(null)}
                style={{ padding: '9px 14px', background: '#fff', color: '#9ca3af', border: '1.5px solid #e5e7eb', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main panel */}
      <div style={{
        background: dueMessages.length > 0 ? '#fffbeb' : '#f0fdf9',
        border: `1.5px solid ${dueMessages.length > 0 ? '#fcd34d' : '#6ee7b7'}`,
        borderRadius: 10, marginBottom: 14, overflow: 'hidden',
      }}>
        {/* Header */}
        <div
          style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setExpanded(e => !e)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{dueMessages.length > 0 ? '📬' : '📅'}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                KIT Scheduled Messages
                {dueMessages.length > 0 && (
                  <span style={{ background: '#ef4444', color: '#fff', borderRadius: 12, padding: '1px 8px', fontSize: 11 }}>
                    {dueMessages.length} Due Now
                  </span>
                )}
                {upcomingMessages.length > 0 && (
                  <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 12, padding: '1px 8px', fontSize: 11 }}>
                    {upcomingMessages.length} Due Soon
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                {dueMessages.length > 0
                  ? 'Send these WhatsApp messages to continue your KIT programme'
                  : `${upcomingMessages.length} message${upcomingMessages.length !== 1 ? 's' : ''} coming up in the next 3 days`}
              </div>
            </div>
          </div>
          <span style={{ color: '#94a3b8', fontSize: 14 }}>{expanded ? '▲' : '▼'}</span>
        </div>

        {expanded && (
          <div style={{ borderTop: '1px solid #e5e7eb', maxHeight: 340, overflowY: 'auto' }}>
            {/* Due messages */}
            {dueMessages.map(({ lead, msg }) => {
              const overdueDays = Math.floor((Date.now() - new Date(msg.scheduledDate)) / 86400000);
              const name = lead.enquiredForName || lead.name || 'Unknown';
              return (
                <div key={`${lead._id}_${msg.scheduleId}`} style={{
                  padding: '9px 14px', borderBottom: '1px solid #fef3c7',
                  display: 'flex', alignItems: 'flex-start', gap: 10, background: '#fff',
                }}>
                  <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1.4 }}>{msg.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 12 }}>{name}</span>
                      <span style={chipStyle(msg.type)}>{msg.type}</span>
                      {overdueDays > 0 && (
                        <span style={{ fontSize: 10, background: '#fee2e2', color: '#b91c1c', padding: '1px 6px', borderRadius: 10 }}>
                          {overdueDays}d overdue
                        </span>
                      )}
                      {overdueDays === 0 && (
                        <span style={{ fontSize: 10, background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 10 }}>
                          Due today
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>{msg.title}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4, marginTop: 2 }}>
                      {msg.text.slice(0, 90)}{msg.text.length > 90 ? '…' : ''}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                      Day {msg.day} · {lead.number} · Scheduled {new Date(msg.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                    <button
                      onClick={() => handleSendClick(lead, msg)}
                      style={{ padding: '5px 11px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      💬 Send
                    </button>
                    <button
                      onClick={() => onSkip(lead, msg.scheduleId)}
                      style={{ padding: '4px 11px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                    >
                      Skip
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Upcoming divider */}
            {upcomingMessages.length > 0 && (
              <div style={{ padding: '6px 14px', background: '#f8fafc', fontSize: 11, color: '#64748b', fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>
                📅 UPCOMING — NEXT 3 DAYS
              </div>
            )}

            {/* Upcoming messages */}
            {upcomingMessages.map(({ lead, msg }) => {
              const daysUntil = Math.ceil((new Date(msg.scheduledDate) - Date.now()) / 86400000);
              const name = lead.enquiredForName || lead.name || 'Unknown';
              return (
                <div key={`up_${lead._id}_${msg.scheduleId}`} style={{
                  padding: '7px 14px', borderBottom: '1px solid #f1f5f9',
                  display: 'flex', alignItems: 'center', gap: 10, background: '#fafafa',
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0, opacity: 0.7 }}>{msg.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{name} — {msg.title}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                      Day {msg.day} · Due in {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 8, flexShrink: 0 }}>
                    {new Date(msg.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
